<!-- Video dialog, "Sources and references" (#videoRefs, under the notes): the sources and saved videos linked to the
     video (form.sourceIds / form.savedIds, saved with the video). Each one with its image or icon, title and kind; a tap
     opens it (source or saved video dialog, above this one), the arrow opens its link in a new tab, × unlinks it (the
     item stays in its page). "Link" opens RefsPickDialog.vue; a link pasted in the field becomes a new source or a new
     saved video of the video's project, linked at once (the button suggested from the link is in the main colour).
     Logic in src/features/videoRefs.ts. -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { useProfileData } from '../../composables/useProfileData'
import { useDataStore } from '../../stores/data'
import { useDialogsStore } from '../../stores/dialogs'
import { saveAppData } from '../../features/storage/save'
import { linkedRefs, quickRef, refKindOfLink, toggleRef, type RefGroup } from '../../features/videoRefs'
import { faviconUrl, sourceDomain, sourceThumb, sourceUrl } from '../../features/sources'
import { platformInfo, savedPlatform, savedThumb, savedTitle, savedUrl } from '../../features/savedVideos'
import { kindInfo } from '../../features/categories'
import { isLookImg } from '../../features/looks'
import { openHref } from '../../utils/url'
import { bgImage } from '../../utils/css'
import { readableHex } from '../../features/theme'
import type { VideoForm } from '../../features/video'

const props = defineProps<{ form: VideoForm }>()
const { t } = useI18n()
const data = useProfileData()
const dialogs = useDialogsStore()

const linked = computed(() => linkedRefs(data.value, props.form))
const rows = computed(() => [
  ...linked.value.sources.map(s => {
    const k = kindInfo(data.value, 'src', s.kind), dom = sourceDomain(s)
    return { g: 'src' as RefGroup, id: s.id, title: s.title, sub: [k?.name, dom].filter(Boolean).join(' · ') || t('Source'), url: sourceUrl(s),
      thumb: sourceThumb(s), favicon: dom ? faviconUrl(dom) : '', img: k && isLookImg(k.icon) ? k.icon : '', icon: k && !isLookImg(k.icon) ? 'fas ' + k.icon : 'fas fa-link', color: '' }
  }),
  ...linked.value.saved.map(v => {
    const p = platformInfo(savedPlatform(v)), k = kindInfo(data.value, 'saved', v.kind)
    return { g: 'saved' as RefGroup, id: v.id, title: savedTitle(v), sub: [t('Saved video'), p.name, k?.name].filter(Boolean).join(' · '), url: savedUrl(v),
      thumb: savedThumb(v), favicon: '', img: '', icon: p.icon, color: p.color ? readableHex(p.color) : '' }
  })
])
const open = (g: RefGroup, id: string) => (g === 'src' ? dialogs.show('source', { id }) : dialogs.show('saved', { id }))
const hideImg = (e: Event) => { (e.target as HTMLImageElement).style.visibility = 'hidden' }

// A pasted link: a new source or a new saved video
const link = ref('')
const guess = computed(() => refKindOfLink(link.value))
function quick(g: RefGroup) {
  if (!guess.value) return
  const d = useDataStore().getData()
  if (!quickRef(d, props.form, link.value, g, props.form.projectId)) return
  saveAppData(d)
  link.value = ''
}
const btn = (g: RefGroup) => ['px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap disabled:opacity-40', guess.value === g ? 'bg-brand hover:bg-brandDark text-white' : 'bg-darkbtn text-gray-300 hover:text-brand']
</script>

<template>
  <div id="videoRefs">
    <div class="flex items-center justify-between gap-2 mb-2">
      <label class="text-xs font-bold text-gray-400 tracking-wider">{{ t('SOURCES AND REFERENCES') }}</label>
      <button type="button" class="text-xs font-bold text-brand hover:underline" @click="dialogs.show('refsPick', {})"><i class="fas fa-link mr-1"></i>{{ t('Link sources and saved videos') }}</button>
    </div>
    <div v-if="rows.length" class="space-y-1.5 mb-2">
      <div v-for="r in rows" :key="r.g + r.id" class="flex items-center gap-2.5 p-2 rounded-xl bg-darkinput border border-gray-800">
        <span v-if="r.thumb" class="w-10 h-10 rounded-lg bg-cover bg-center flex-shrink-0" :style="bgImage(r.thumb)"></span>
        <span v-else class="w-10 h-10 rounded-lg bg-thumbBg flex-shrink-0 flex items-center justify-center" :style="r.color ? { color: r.color } : undefined">
          <img v-if="r.favicon" :src="r.favicon" alt="" class="w-5 h-5" @error="hideImg"><img v-else-if="r.img" :src="r.img" alt="" class="w-6 h-6 rounded object-cover"><i v-else :class="[r.icon, 'text-brand']"></i></span>
        <button type="button" class="flex-1 min-w-0 text-left" :title="t('Open')" @click="open(r.g, r.id)">
          <span class="block text-sm font-bold text-gray-100 truncate">{{ r.title }}</span>
          <span class="block text-[11px] text-gray-500 truncate"><i :class="[r.g === 'src' ? 'fas fa-book-open' : 'fas fa-bookmark', 'mr-1']"></i>{{ r.sub }}</span>
        </button>
        <a v-if="r.url" :href="openHref(r.url)" target="_blank" rel="noopener" :title="r.g === 'src' ? t('Open the site') : t('Watch the video')" class="w-8 h-8 rounded-lg bg-darkbtn text-gray-400 hover:text-brand flex items-center justify-center flex-shrink-0"><i :class="['fas', r.g === 'src' ? 'fa-arrow-up-right-from-square' : 'fa-play', 'text-xs']"></i></a>
        <button type="button" :title="t('Unlink (it stays in its page)')" class="w-8 h-8 rounded-lg text-gray-500 hover:text-brand flex-shrink-0" @click="toggleRef(form, r.g, r.id)"><i class="fas fa-xmark"></i></button>
      </div>
    </div>
    <div class="flex flex-wrap gap-2">
      <input id="videoRefLink" v-model="link" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off" :placeholder="t('Paste a link to add it here')" class="flex-1 min-w-[12rem] bg-darkinput border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-brand"
        @keydown.enter.prevent="guess && quick(guess)">
      <button type="button" :disabled="!guess" :class="btn('src')" @click="quick('src')"><i class="fas fa-plus mr-1"></i>{{ t('As a source') }}</button>
      <button type="button" :disabled="!guess" :class="btn('saved')" @click="quick('saved')"><i class="fas fa-plus mr-1"></i>{{ t('As a saved video') }}</button>
    </div>
  </div>
</template>
